import React from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import ReactMarkdown from "react-markdown";
import blogs from "./blogData";
import "../Styles/Blog.css";

const calculateReadTime = (content) => {
  const wordsPerMinute = 200;
  const words = content.trim().split(/\s+/).length;
  return Math.ceil(words / wordsPerMinute);
};

export default function BlogPost() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  const { slug } = useParams();
  const blog = blogs.find((b) => b.slug === slug);

  if (!blog) {
    return (
      <div className="blog-not-found container">
        <h2>Blog post not found.</h2>
        <Link to="/blog" className="lmbtn">← Back to Blog</Link>
      </div>
    );
  }

  const readTime = calculateReadTime(blog.content);

  // Get related posts (exclude current, take 3 randomly or from same category)
  const relatedPosts = blogs
    .filter((b) => b.slug !== slug)
    .sort(() => 0.5 - Math.random())
    .slice(0, 3);

  const shareUrl = `https://veabroad.com/blog/${blog.slug}`;
  const shareTitle = encodeURIComponent(blog.title);

  return (
    <>
      <Helmet>
        <title>{blog.title} | VE Abroad</title>
        <meta name="description" content={blog.excerpt} />
        <link rel="canonical" href={`https://veabroad.com/blog/${blog.slug}`} />
        <meta property="og:title" content={`${blog.title} | VE Abroad`} />
        <meta property="og:description" content={blog.excerpt} />
        <meta property="og:image" content={blog.image} />
        <meta property="og:url" content={`https://veabroad.com/blog/${blog.slug}`} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${blog.title} | VE Abroad`} />
        <meta name="twitter:description" content={blog.excerpt} />
        <meta name="twitter:image" content={blog.image} />
        <meta name="keywords" content={`${blog.category}, study abroad, immigration, visa, VE Abroad`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": blog.title,
            "description": blog.excerpt,
            "image": blog.image,
            "datePublished": blog.date,
            "author": {
              "@type": "Organization",
              "name": "Vidya Education Abroad"
            },
            "publisher": {
              "@type": "EducationalOrganization",
              "name": "Vidya Education Abroad",
              "logo": {
                "@type": "ImageObject",
                "url": "https://veabroad.com/Logo.webp"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://veabroad.com/blog/${blog.slug}`
            },
            "wordCount": blog.content.split(/\s+/).length
          })}
        </script>
      </Helmet>

      <article className="blog-post container">
        <Link id="blog-post-back-to-blog-link" to="/blog" className="blog-back-link">← Back to Blog</Link>
        <div className="blog-post-header">
          <span className="blog-category">{blog.category}</span>
          <span className="blog-read-time">{readTime} min read</span>
        </div>
        <h1>{blog.title}</h1>
        <div className="blog-author-meta">
          <p className="blog-date">{blog.date} • By Vidya Education Abroad Team</p>
        </div>
        <img src={blog.image} alt={blog.title} className="blog-post-hero-img" loading="eager" />
        
        <div className="blog-social-share">
          <span>Share this article:</span>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer" className="share-icon facebook">Facebook</a>
          <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}&title=${shareTitle}`} target="_blank" rel="noopener noreferrer" className="share-icon linkedin">LinkedIn</a>
          <a href={`https://api.whatsapp.com/send?text=${shareTitle} ${shareUrl}`} target="_blank" rel="noopener noreferrer" className="share-icon whatsapp">WhatsApp</a>
        </div>

        <div className="blog-post-content">
          <ReactMarkdown>{blog.content}</ReactMarkdown>
        </div>
        <div className="blog-cta">
          <p>Need expert guidance? Talk to our consultants for free.</p>
          <Link id="blog-post-consultation-link" to="/consultationForm" className="lmbtn">Get Free Consultation</Link>
        </div>

        <div className="related-posts-section">
          <h2>Related Articles</h2>
          <div className="row g-4">
            {relatedPosts.map((relatedBlog) => (
              <div className="col-md-4" key={relatedBlog.slug}>
                <Link to={`/blog/${relatedBlog.slug}`} className="blog-card-link">
                  <div className="blog-card">
                    <img src={relatedBlog.image} alt={relatedBlog.title} loading="lazy" />
                    <div className="blog-card-body">
                      <span className="blog-category">{relatedBlog.category}</span>
                      <h3>{relatedBlog.title}</h3>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </article>
    </>
  );
}
